/**
 * Crew-Lead Labor Actuals (PR4)
 * ─────────────────────────────
 * Append-only capture of actual hours by role at job closeout. Pure
 * builders + a tiny reducer; the AppDataContext holds the array and
 * exposes a setter, but ALL mutations route through these helpers so
 * the audit shape stays consistent.
 *
 * Hard rules:
 *   • Append-only. No edits, no deletes. Corrections = NEW entry with
 *     `supersedesId` pointing back. Resolver reads latest non-superseded.
 *   • Crew-Lead-attributed. `enteredBy` is required and stored verbatim.
 *   • NEVER references contractValue. Labor cost only.
 *   • Computation: `actualDollars = Σ (hours × loadedRate)` using the
 *     canonical `getLoadedRate()` — never inline rates.
 */

import { getLoadedRate, type InternalCrewRole } from "./internalLaborRates";
import type { LaborCostActualInput } from "./getJobPayRun";

export interface LaborActualLine {
  role: InternalCrewRole;
  hours: number;
}

export interface LaborActualRecord {
  id: string;
  jobId: string;
  lines: Array<LaborActualLine & { loadedRate: number; dollars: number }>;
  actualDollars: number;
  enteredBy: string;
  enteredAt: string;
  /** If this entry corrects a prior one. */
  supersedesId?: string;
  /** "actual_manual" | "payroll_import" — defaults to actual_manual. */
  source: "actual_manual" | "payroll_import";
  /** Optional free-text note from the Crew Lead. */
  note?: string;
}

export interface RecordLaborActualInput {
  jobId: string;
  lines: LaborActualLine[];
  enteredBy: string;
  enteredAt?: string;
  supersedesId?: string;
  source?: "actual_manual" | "payroll_import";
  note?: string;
}

export type RecordLaborActualResult =
  | { ok: true; record: LaborActualRecord }
  | { ok: false; reason: "no_lines" | "invalid_hours" | "missing_entered_by" };

/** Pure builder. The caller appends to its store. */
export function recordLaborActual(input: RecordLaborActualInput): RecordLaborActualResult {
  if (!input.enteredBy) return { ok: false, reason: "missing_entered_by" };
  if (!input.lines.length) return { ok: false, reason: "no_lines" };
  if (input.lines.some((l) => !Number.isFinite(l.hours) || l.hours < 0)) {
    return { ok: false, reason: "invalid_hours" };
  }

  const lines = input.lines.map((l) => {
    const loadedRate = getLoadedRate(l.role);
    return { ...l, loadedRate, dollars: l.hours * loadedRate };
  });
  const actualDollars = lines.reduce((s, l) => s + l.dollars, 0);

  const record: LaborActualRecord = {
    id: `labor-actual-${input.jobId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    jobId: input.jobId,
    lines,
    actualDollars,
    enteredBy: input.enteredBy,
    enteredAt: input.enteredAt ?? new Date().toISOString(),
    supersedesId: input.supersedesId,
    source: input.source ?? "actual_manual",
    note: input.note,
  };
  return { ok: true, record };
}

/**
 * Reduce a job's append-only history to the latest effective record
 * (most recent that has not been superseded). Returns `undefined` when
 * none exist.
 */
export function getEffectiveLaborActual(
  jobId: string,
  records: LaborActualRecord[],
): LaborActualRecord | undefined {
  const forJob = records.filter((r) => r.jobId === jobId);
  if (!forJob.length) return undefined;
  const supersededIds = new Set(forJob.map((r) => r.supersedesId).filter((x): x is string => !!x));
  const live = forJob.filter((r) => !supersededIds.has(r.id));
  return live.sort((a, b) => b.enteredAt.localeCompare(a.enteredAt))[0];
}

/** Project an effective record into the resolver's input shape. */
export function toLaborCostActualInput(
  record: LaborActualRecord,
): LaborCostActualInput {
  return {
    actualDollars: record.actualDollars,
    byRole: record.lines.map((l) => ({
      role: l.role,
      hours: l.hours,
      loadedRate: l.loadedRate,
    })),
    source: record.source,
    enteredBy: record.enteredBy,
    enteredAt: record.enteredAt,
  };
}
